import { FC, Fragment, useMemo } from 'react';
import { IVisualTextBoxes } from 'src/ts/interfaces/listCheck';
import {
  FontCheckHowTo,
  FontCheckLabel,
  HeaderBarBox,
  HeaderBarPage,
  HeaderBarRow,
  HeaderBarSign,
  HeaderBarText,
  ListCheckEmptyState
} from '../../../../styles';
import { listCheckCopy } from '../../listCheckCopy';
import { PaneNotice } from '../../textBoxes/styles';
import { headerLines, headerSummary, separatorBars } from '../advancedFraudTrendsModel';
import { ExpectedLine } from '../styles';
import { PatternViewProps, visualNotice } from './types';

const copy = listCheckCopy.advancedFraudTrends;

const DEFAULT_BAR = '!';

const HeaderBarsView: FC<PatternViewProps> = ({ entry, pattern, visual }) => {
  const notice = visualNotice(entry, visual);
  const expectedBar = pattern.expected?.bar ?? DEFAULT_BAR;
  const data = notice ? null : (visual.data as unknown as IVisualTextBoxes | null);
  const lines = useMemo(() => headerLines(data, expectedBar), [data, expectedBar]);
  const separators = useMemo(() => separatorBars(data, expectedBar), [data, expectedBar]);
  const editedLine = lines.find((line) => line.pieces.some((piece) => piece.edited));

  if (notice) {
    return (
      <div data-testid="advanced-fraud-trends-header-bars">
        <PaneNotice data-testid="advanced-fraud-trends-page-view-notice">{notice}</PaneNotice>
      </div>
    );
  }

  return (
    <div data-testid="advanced-fraud-trends-header-bars">
      {lines.length ? (
        <>
          <FontCheckHowTo data-testid="advanced-fraud-trends-header-summary">
            {headerSummary(lines, expectedBar)}{' '}
            {copy.headerBarsHowTo((editedLine ?? lines[0]).page)}
          </FontCheckHowTo>
          <HeaderBarBox>
            {pattern.expected && (
              <>
                <FontCheckLabel>{copy.expectedStatement}</FontCheckLabel>
                <ExpectedLine data-testid="advanced-fraud-trends-expected-line">
                  {pattern.expected.line}
                </ExpectedLine>
              </>
            )}
            <FontCheckLabel>{listCheckCopy.thisDocument}</FontCheckLabel>
            {lines.map((line) => (
              <HeaderBarRow key={line.page}>
                <HeaderBarPage>{listCheckCopy.page(line.page)}</HeaderBarPage>
                <HeaderBarText data-testid="advanced-fraud-trends-header-line">
                  {line.pieces.map((piece, index) => {
                    const previous = line.pieces[index - 1];
                    return (
                      <Fragment key={piece.id}>
                        {previous && (previous.row === piece.row ? ' ' : '\n')}
                        {piece.bar ? (
                          <HeaderBarSign
                            $bad={piece.edited}
                            data-testid="advanced-fraud-trends-header-bar"
                            data-edited={piece.edited}
                          >
                            {piece.text}
                          </HeaderBarSign>
                        ) : (
                          piece.text
                        )}
                      </Fragment>
                    );
                  })}
                </HeaderBarText>
              </HeaderBarRow>
            ))}
          </HeaderBarBox>
        </>
      ) : (
        <ListCheckEmptyState>{copy.noHeaderLine}</ListCheckEmptyState>
      )}
      {separators.length > 0 && (
        <HeaderBarBox data-testid="advanced-fraud-trends-separators">
          <FontCheckLabel>{copy.separatorBars}</FontCheckLabel>
          <FontCheckLabel>{copy.expectedStatement}</FontCheckLabel>
          <ExpectedLine>{expectedBar.repeat(separators.length)}</ExpectedLine>
          <FontCheckLabel>{copy.documentBars(separators.length)}</FontCheckLabel>
          <HeaderBarText>
            {separators.map((separator, index) => (
              <HeaderBarSign
                key={index}
                $bad={separator.edited}
                data-testid="advanced-fraud-trends-separator"
                data-edited={separator.edited}
              >
                {separator.copiesAs}
              </HeaderBarSign>
            ))}
          </HeaderBarText>
        </HeaderBarBox>
      )}
    </div>
  );
};

export default HeaderBarsView;
